import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/utils/combine';
import { attachClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/attach-closest-edge';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/extract-closest-edge';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/types';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type BoardDetail,
  type Label,
  LabelChip,
  LabelChipPicker,
  useMoveCardLabel,
  useSetCardLabel,
} from '@/entities/board';
import type { CardDetail } from '@/entities/card';
import { FormError } from '@/shared/forms';
import { dropMove } from '@/shared/lib/reorder';
import { HelpRow } from '@/shared/ui';

import styles from './Labels.module.css';

const labelData = (cardId: string, labelId: string) => ({ type: 'card-label', cardId, labelId });

function isCardLabel(
  data: Record<string | symbol, unknown>,
): data is Record<string | symbol, unknown> & ReturnType<typeof labelData> {
  return (
    data.type === 'card-label' &&
    typeof data.cardId === 'string' &&
    typeof data.labelId === 'string'
  );
}

/**
 * Labels of one card: the assigned labels in the card's own order (reordered by
 * drag and drop only) and a `+` picker that only assigns/unassigns the
 * board's existing labels. Creating and editing labels is a board-level action.
 */
export function LabelPicker({ card, board }: { card: CardDetail; board: BoardDetail }) {
  const { t } = useTranslation();
  const boardId = board.board.id;
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const setLabel = useSetCardLabel(boardId);
  const moveLabel = useMoveCardLabel(boardId);
  // The board preview is updated optimistically, so it is the freshest source.
  // `label_ids` is already in the card's own order.
  const assigned = board.cards.find((c) => c.id === card.id)?.label_ids ?? card.label_ids;
  const assignedLabels = assigned
    .map((id) => board.labels.find((label) => label.id === id))
    .filter((label): label is Label => label !== undefined);
  const error = setLabel.error ?? moveLabel.error;

  // Always sees the latest order without re-subscribing the monitor.
  const onDrop = useEffectEvent((labelId: string, targetId: string, edge: Edge | null) => {
    const result = dropMove(
      assigned,
      labelId,
      targetId,
      edge === 'left' ? 'before' : 'after',
      'label',
    );
    if (!result) return;
    moveLabel.mutate({ cardId: card.id, labelId, anchor: result.anchor, order: result.order });
  });
  useEffect(
    () =>
      monitorForElements({
        canMonitor: ({ source }) => isCardLabel(source.data) && source.data.cardId === card.id,
        onDrop: ({ source, location }) => {
          const target = location.current.dropTargets[0];
          if (!target || !isCardLabel(source.data) || !isCardLabel(target.data)) return;
          onDrop(source.data.labelId, target.data.labelId, extractClosestEdge(target.data));
        },
      }),
    [card.id],
  );

  // Outside = anywhere but the picker itself (an empty spot of the card dialog included).
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && rootRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [open]);

  return (
    <section ref={rootRef} className={styles.section} aria-labelledby={`labels-${card.id}`}>
      <HelpRow topic={t('labels.title')} topicId="labels" text={t('help.labels')}>
        <h3 id={`labels-${card.id}`} className={styles.title}>
          {t('labels.title')}
        </h3>
      </HelpRow>
      <ul className={styles.chips} aria-label={t('labels.assigned')}>
        {assignedLabels.map((label) => (
          <AssignedLabel
            key={label.id}
            cardId={card.id}
            label={label}
            reorderable={assignedLabels.length > 1}
          />
        ))}
        <li>
          <button
            ref={addRef}
            type="button"
            className={styles.add}
            aria-label={t('labels.add')}
            title={t('labels.add')}
            aria-haspopup="true"
            aria-expanded={open}
            aria-controls={`label-picker-${card.id}`}
            onClick={() => {
              setOpen((value) => !value);
            }}
          >
            +
          </button>
        </li>
      </ul>
      {assignedLabels.length === 0 && !open && <p className={styles.empty}>{t('labels.none')}</p>}
      {assignedLabels.length > 1 && <p className={styles.empty}>{t('labels.orderHint')}</p>}
      <FormError error={error} />

      {open && (
        <div
          id={`label-picker-${card.id}`}
          className={styles.popover}
          role="dialog"
          aria-label={t('labels.pickerTitle')}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              // Close only the picker, not the card dialog.
              event.preventDefault();
              event.stopPropagation();
              setOpen(false);
              addRef.current?.focus();
            }
          }}
        >
          {/* Applies immediately and stays open: several labels in a row. */}
          <LabelChipPicker
            labels={board.labels}
            selectedIds={assigned}
            label={t('labels.pickerTitle')}
            emptyText={t('labels.pickerEmpty')}
            onToggle={(label, selected) => {
              setLabel.mutate({ cardId: card.id, labelId: label.id, assigned: selected });
            }}
          />
        </div>
      )}
    </section>
  );
}

interface AssignedLabelProps {
  cardId: string;
  label: Label;
  reorderable: boolean;
}

function AssignedLabel({ cardId, label, reorderable }: AssignedLabelProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLLIElement>(null);
  const [dragging, setDragging] = useState(false);
  const [edge, setEdge] = useState<Edge | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || !reorderable) return;
    return combine(
      draggable({
        element,
        getInitialData: () => labelData(cardId, label.id),
        onDragStart: () => {
          setDragging(true);
        },
        onDrop: () => {
          setDragging(false);
        },
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) =>
          isCardLabel(source.data) &&
          source.data.cardId === cardId &&
          source.data.labelId !== label.id,
        getData: ({ input }) =>
          attachClosestEdge(labelData(cardId, label.id), {
            element,
            input,
            allowedEdges: ['left', 'right'],
          }),
        onDrag: ({ self }) => {
          setEdge(extractClosestEdge(self.data));
        },
        onDragLeave: () => {
          setEdge(null);
        },
        onDrop: () => {
          setEdge(null);
        },
      }),
    );
  }, [cardId, label.id, reorderable]);

  return (
    <li
      ref={ref}
      className={[
        styles.assigned,
        reorderable && styles.draggable,
        dragging && styles.dragging,
        edge && styles[`drop-${edge}`],
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="assigned-label"
    >
      <LabelChip label={label} emptyName={t('labels.unnamed')} />
    </li>
  );
}
